"use client";

/**
 * Pricing helpers for the console: the site's MPAN, and pounds per half hour
 * from POST /api/trading/cost/price (the C1 engine behind price_energy).
 *
 * Nothing here invents a rate. A half hour the engine could not fully price
 * comes back as `{ priced: false, reason }` naming the missing component, and
 * a site with no MPAN row is `NO_MPAN_REASON` for every half hour.
 */

import { useEffect, useState } from "react";
import { type CostBeside, NO_MPAN_REASON, notPriced } from "@/components/sites/EnergyCost";
import { type MpanConfig, CostApiError, getMpans, priceItems } from "@/lib/cost-api";

export type SiteMpan =
  | { state: "loading" }
  | { state: "ready"; mpan: string; config: MpanConfig }
  | { state: "none"; reason: string };

/** The MPAN configuration linked to a site (workspace_id), from the organisation's own rows. */
export function useSiteMpan(siteId: string | null | undefined): SiteMpan {
  const [value, setValue] = useState<SiteMpan>({ state: "loading" });
  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;
    setValue({ state: "loading" });
    getMpans()
      .then((res) => {
        if (cancelled) return;
        const rows = res.mpans.filter((m) => m.workspace_id === siteId).sort((a, b) => (a.effective_from < b.effective_from ? 1 : -1));
        if (rows.length === 0) setValue({ state: "none", reason: NO_MPAN_REASON });
        else setValue({ state: "ready", mpan: rows[0].mpan, config: rows[0] });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const detail = err instanceof CostApiError ? `${err.status} ${err.message}` : err instanceof Error ? err.message : "request failed";
        setValue({ state: "none", reason: `not priced: the charge library could not be read (${detail})` });
      });
    return () => {
      cancelled = true;
    };
  }, [siteId]);
  return value;
}

export type HalfHourToPrice = { key: string; period_start: string; kwh: number; direction: "import" | "export" };

const BATCH = 2000;

/** Price half hours at an MPAN. Returns one CostBeside per key; a zero kWh half hour is priced at £0.00 without a lookup. */
export async function priceHalfHours(mpan: string, items: HalfHourToPrice[]): Promise<Map<string, CostBeside>> {
  const out = new Map<string, CostBeside>();
  const toPrice = items.filter((i) => Number.isFinite(i.kwh) && i.kwh > 0);
  for (const i of items) {
    if (!(Number.isFinite(i.kwh) && i.kwh > 0)) out.set(i.key, { priced: true, gbp: 0, status: "nothing to price" });
  }
  for (let start = 0; start < toPrice.length; start += BATCH) {
    const chunk = toPrice.slice(start, start + BATCH);
    let res;
    try {
      res = await priceItems(mpan, chunk.map((i) => ({ period_start: i.period_start, kwh: i.kwh, direction: i.direction })));
    } catch (err: unknown) {
      const detail = err instanceof CostApiError ? `${err.status} ${err.message}` : err instanceof Error ? err.message : "request failed";
      for (const i of chunk) out.set(i.key, notPriced(`the pricing engine could not be reached (${detail})`));
      continue;
    }
    res.results.forEach((r, idx) => {
      const item = chunk[idx];
      if (!item) return;
      if (r.priced && r.gbp !== null) {
        const statuses = Array.from(new Set(r.lines.map((l) => l.status).filter((s) => s !== "not_applicable")));
        out.set(item.key, { priced: true, gbp: r.gbp, status: statuses.length === 1 ? statuses[0] : "mixed", source: r.tariff_version });
      } else {
        const missing = r.unpriced.map((u) => `${u.label} (${u.reason})`).join(", ") || r.reason || "unknown";
        out.set(item.key, notPriced(missing));
      }
    });
  }
  return out;
}

const LONDON_PARTS = new Intl.DateTimeFormat("en-GB", { timeZone: "Europe/London", weekday: "short", hour: "2-digit", minute: "2-digit", hourCycle: "h23", year: "numeric", month: "2-digit" });

/** The rate slot of a half hour: its Europe/London month, weekday or weekend, and time of day. */
export function rateSlotOf(periodStart: string): string {
  const p = Object.fromEntries(LONDON_PARTS.formatToParts(new Date(periodStart)).map((x) => [x.type, x.value]));
  const kind = p.weekday === "Sat" || p.weekday === "Sun" ? "weekend" : "weekday";
  return `${p.year}-${p.month}|${kind}|${p.hour}:${p.minute}`;
}

/**
 * Price many half hours cheaply: the charge engine prices every component per kWh, so a half hour costs
 * its kWh times the unit price of its rate slot. One kWh is priced once per (month, weekday or
 * weekend, half hour of day, direction) and applied to each half hour's own kWh. Used for days and
 * months, where thousands of half hours would take minutes to send one by one; the half hourly view
 * still prices every half hour itself.
 */
export async function priceBySlot(mpan: string, items: HalfHourToPrice[]): Promise<Map<string, CostBeside>> {
  const out = new Map<string, CostBeside>();
  const slots = new Map<string, HalfHourToPrice>();
  for (const i of items) {
    if (!(Number.isFinite(i.kwh) && i.kwh > 0)) {
      out.set(i.key, { priced: true, gbp: 0, status: "nothing to price" });
      continue;
    }
    const slot = `${rateSlotOf(i.period_start)}|${i.direction}`;
    if (!slots.has(slot)) slots.set(slot, { key: slot, period_start: i.period_start, kwh: 1, direction: i.direction });
  }
  const unit = await priceHalfHours(mpan, [...slots.values()]);
  for (const i of items) {
    if (out.has(i.key)) continue;
    const u = unit.get(`${rateSlotOf(i.period_start)}|${i.direction}`);
    if (!u) out.set(i.key, notPriced("no rate lookup was made for this half hour"));
    else if (!u.priced) out.set(i.key, u);
    else out.set(i.key, { priced: true, gbp: u.gbp * i.kwh, status: u.status, source: u.source });
  }
  return out;
}
